import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function normalizeRedirectTarget(target) {
  if (!target || typeof target !== "string") {
    return "/";
  }

  return target.startsWith("/") ? target : "/";
}

export default function CompletePhonePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const redirectTarget = normalizeRedirectTarget(
    searchParams.get("redirect") || sessionStorage.getItem("redirectAfterLogin") || "/",
  );

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Failed to load your profile");
        }

        if (cancelled) {
          return;
        }

        const nextProfile = {
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          phone: data.phone || "",
        };

        if (String(nextProfile.phone).trim()) {
          navigate(redirectTarget, { replace: true });
          return;
        }

        setProfile(nextProfile);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [navigate, redirectTarget]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const trimmedPhone = profile.phone.trim();
    if (!trimmedPhone) {
      setError("Phone number is required.");
      return;
    }

    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: trimmedPhone }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to save your phone number");
      }

      sessionStorage.removeItem("redirectAfterLogin");
      window.dispatchEvent(new Event("authStateChanged"));
      navigate(redirectTarget, { replace: true });
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f7fbff 0%, #eef6fb 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          background: "#ffffff",
          borderRadius: "18px",
          padding: "32px",
          boxShadow: "0 18px 60px rgba(45, 109, 163, 0.12)",
          border: "1px solid #dbe8f3",
        }}
      >
        <p
          style={{
            margin: "0 0 8px 0",
            color: "#2d6da3",
            fontWeight: 700,
            letterSpacing: "0.08em",
            fontSize: "12px",
            textTransform: "uppercase",
          }}
        >
          Profile Required
        </p>
        <h1 style={{ margin: "0 0 12px 0", fontSize: "30px", color: "#102a43" }}>
          Add your phone number
        </h1>
        <p style={{ margin: "0 0 24px 0", color: "#486581", lineHeight: 1.6 }}>
          A phone number is required before you can continue. We use it for booking updates,
          appointment reminders, and support when needed.
        </p>

        {loading ? (
          <p style={{ margin: 0, color: "#486581" }}>Loading your profile...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gap: "14px" }}>
              <input
                type="text"
                value={profile.firstName}
                readOnly
                aria-label="First name"
                style={{
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border: "1px solid #d9e2ec",
                  background: "#f8fbfd",
                }}
              />
              <input
                type="text"
                value={profile.lastName}
                readOnly
                aria-label="Last name"
                style={{
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border: "1px solid #d9e2ec",
                  background: "#f8fbfd",
                }}
              />
              <input
                type="email"
                value={profile.email}
                readOnly
                aria-label="Email"
                style={{
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border: "1px solid #d9e2ec",
                  background: "#f8fbfd",
                }}
              />
              <input
                type="tel"
                value={profile.phone}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
                placeholder="Phone number"
                autoFocus
                required
                aria-label="Phone number"
                style={{
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border: `1px solid ${error ? "#d64545" : "#bcccdc"}`,
                  outline: "none",
                }}
              />
            </div>

            {error ? (
              <div
                style={{
                  marginTop: "14px",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  background: "#fff1f2",
                  color: "#b42318",
                  border: "1px solid #fecdd3",
                }}
              >
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={saving}
              style={{
                width: "100%",
                marginTop: "20px",
                padding: "14px 16px",
                borderRadius: "12px",
                border: "none",
                background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "15px",
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              {saving ? "Saving..." : "Save and Continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}